import { useCallback, useState } from 'react';

const STORAGE_KEY_PREFIX = 'platformV2.columnVisibility.';

function readHiddenColumns(storageKey: string, columnIds: string[], defaultHiddenColumnIds: string[]): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) {
      return new Set(defaultHiddenColumnIds);
    }
    const stored: unknown = JSON.parse(raw);
    return new Set(Array.isArray(stored) ? columnIds.filter(id => stored.includes(id)) : []);
  } catch {
    return new Set(defaultHiddenColumnIds);
  }
}

function writeHiddenColumns(storageKey: string, hidden: Set<string>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...hidden]));
  } catch {
    // Storage may be unavailable or full: visibility just won't persist.
  }
}

/**
 * Tracks hidden columns of a list and persists them in localStorage under a per-list key.
 * `defaultHiddenColumnIds` are hidden until the user saves a selection of their own.
 */
export function useColumnVisibility(
  listId: string,
  hideableColumnIds: string[],
  defaultHiddenColumnIds: string[] = []
) {
  const storageKey = `${STORAGE_KEY_PREFIX}${listId}`;
  const [hidden, setHidden] = useState(() => readHiddenColumns(storageKey, hideableColumnIds, defaultHiddenColumnIds));

  const isVisible = useCallback((columnId: string) => !hidden.has(columnId), [hidden]);

  const applyVisibleColumns = useCallback(
    (visibleColumnIds: string[]) => {
      const next = new Set(hideableColumnIds.filter(id => !visibleColumnIds.includes(id)));
      writeHiddenColumns(storageKey, next);
      setHidden(next);
    },
    [hideableColumnIds, storageKey]
  );

  return { applyVisibleColumns, isVisible };
}
